/**
 * DbProductActionBar - 商品详情底部操作栏组件
 *
 * 特性：
 * - 左侧支持动态配置图标按钮（店铺、客服、购物车等）
 * - 右侧支持两种按钮模式：分开(separate) / 组合(combined)
 * - 支持徽标显示
 * - 支持禁用状态
 * - 支持自定义样式
 *
 * 使用示例：
 * ```tsx
 * // 分开模式
 * <DbProductActionBar
 *   leftIcons={[
 *     { id: 'shop', iconName: 'storefront-outline', text: '店铺', onPress: () => {} },
 *     { id: 'service', iconName: 'chatbubble-ellipses-outline', text: '客服', onPress: () => {} },
 *     { id: 'cart', iconName: 'cart-outline', text: '购物车', badge: 3, onPress: () => {} },
 *   ]}
 *   buttonMode="separate"
 *   primaryButton={{ text: '加入购物车', onPress: () => {} }}
 *   secondaryButton={{ text: '立即购买', onPress: () => {} }}
 * />
 *
 * // 组合模式
 * <DbProductActionBar
 *   buttonMode="combined"
 *   primaryButton={{ text: '加入购物车', onPress: () => {} }}
 *   secondaryButton={{ text: '立即购买', onPress: () => {} }}
 * />
 * ```
 */
import React, { useMemo, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  SafeAreaView,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import DbIcon from '../DbIcon';
import { createStyles, ACTION_BAR_DEFAULTS } from './DbProductActionBar.styles';
import type {
  DbProductActionBarProps,
  ActionBarIconItem,
  ActionBarButton,
} from './DbProductActionBar.types';

export const DbProductActionBar: React.FC<DbProductActionBarProps> = ({
  // 左侧图标配置
  leftIcons = [],

  // 右侧按钮配置
  buttonMode = 'separate',
  primaryButton,
  secondaryButton,

  // 容器样式
  height = ACTION_BAR_DEFAULTS.height,
  backgroundColor,
  style,
  leftSectionStyle,
  rightSectionStyle,
  iconItemStyle,
  iconTextStyle,
  buttonTextStyle,

  // 分割线
  showTopDivider = true,
  dividerColor,

  // 定位
  fixed = false,
  safeArea = true,
}) => {
  const insets = useSafeAreaInsets();
  
  // 计算实际需要的高度：基础高度 + 底部安全区域高度
  const totalHeight = safeArea ? height + insets.bottom : height;
  
  const styles = useMemo(
    () => createStyles('light', totalHeight, backgroundColor, dividerColor),
    [totalHeight, backgroundColor, dividerColor]
  );

  /**
   * 渲染徽标
   */
  const renderBadge = useCallback((item: ActionBarIconItem) => {
    if (!item.badge) return null;

    const badgeText = typeof item.badge === 'number' && item.badge > 99
      ? '99+'
      : String(item.badge);

    return (
      <View style={[styles.badge, item.badgeColor && { backgroundColor: item.badgeColor }]}>
        <Text style={styles.badgeText}>{badgeText}</Text>
      </View>
    );
  }, [styles]);

  /**
   * 渲染左侧图标按钮
   */
  const renderLeftIcons = useCallback(() => {
    if (!leftIcons.length) return null;

    return (
      <View style={[styles.leftSection, leftSectionStyle]}>
        {leftIcons.map((item, index) => (
          <TouchableOpacity
            key={item.id || index}
            style={[
              styles.iconItem,
              item.disabled && styles.disabled,
              iconItemStyle,
            ]}
            onPress={item.onPress}
            activeOpacity={0.7}
            disabled={item.disabled}
          >
            <View style={styles.iconContainer}>
              <DbIcon
                name={item.iconName}
                family={item.iconFamily || 'Ionicons'}
                size={item.iconSize || ACTION_BAR_DEFAULTS.iconSize}
                color={item.iconColor || ACTION_BAR_DEFAULTS.iconColor}
              />
              {renderBadge(item)}
            </View>
            {item.text && (
              <Text
                style={[
                  styles.iconText,
                  item.textColor && { color: item.textColor },
                  iconTextStyle,
                ]}
              >
                {item.text}
              </Text>
            )}
          </TouchableOpacity>
        ))}
      </View>
    );
  }, [leftIcons, styles, leftSectionStyle, iconItemStyle, iconTextStyle, renderBadge]);

  /**
   * 渲染右侧分开模式按钮
   */
  const renderSeparateButtons = useCallback(() => {
    return (
      <View style={[styles.rightSection, rightSectionStyle]}>
        <View style={styles.separateContainer}>
          {primaryButton && (
            <TouchableOpacity
              style={[
                styles.separateButton,
                styles.primaryButton,
                primaryButton.backgroundColor && { backgroundColor: primaryButton.backgroundColor },
                primaryButton.disabled && styles.disabled,
              ]}
              onPress={primaryButton.onPress}
              activeOpacity={0.7}
              disabled={primaryButton.disabled}
            >
              <Text style={[styles.buttonText, buttonTextStyle]}>
                {primaryButton.text}
              </Text>
            </TouchableOpacity>
          )}
          {secondaryButton && (
            <TouchableOpacity
              style={[
                styles.separateButton,
                styles.secondaryButton,
                secondaryButton.backgroundColor && { backgroundColor: secondaryButton.backgroundColor },
                secondaryButton.disabled && styles.disabled,
              ]}
              onPress={secondaryButton.onPress}
              activeOpacity={0.7}
              disabled={secondaryButton.disabled}
            >
              <Text style={[styles.buttonText, buttonTextStyle]}>
                {secondaryButton.text}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }, [primaryButton, secondaryButton, styles, rightSectionStyle, buttonTextStyle]);

  /**
   * 渲染右侧组合模式按钮
   */
  const renderCombinedButtons = useCallback(() => {
    return (
      <View style={[styles.rightSection, rightSectionStyle]}>
        <View style={styles.combinedContainer}>
          {primaryButton && (
            <TouchableOpacity
              style={[
                styles.combinedLeftButton,
                primaryButton.backgroundColor && { backgroundColor: primaryButton.backgroundColor },
                primaryButton.disabled && styles.disabled,
              ]}
              onPress={primaryButton.onPress}
              activeOpacity={0.7}
              disabled={primaryButton.disabled}
            >
              <Text style={[styles.buttonText, buttonTextStyle]}>
                {primaryButton.text}
              </Text>
            </TouchableOpacity>
          )}
          {secondaryButton && (
            <TouchableOpacity
              style={[
                styles.combinedRightButton,
                secondaryButton.backgroundColor && { backgroundColor: secondaryButton.backgroundColor },
                secondaryButton.disabled && styles.disabled,
              ]}
              onPress={secondaryButton.onPress}
              activeOpacity={0.7}
              disabled={secondaryButton.disabled}
            >
              <Text style={[styles.buttonText, buttonTextStyle]}>
                {secondaryButton.text}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }, [primaryButton, secondaryButton, styles, rightSectionStyle, buttonTextStyle]);

  /**
   * 渲染右侧按钮区域
   */
  const renderRightButtons = useCallback(() => {
    if (buttonMode === 'combined') {
      return renderCombinedButtons();
    }
    return renderSeparateButtons();
  }, [buttonMode, renderCombinedButtons, renderSeparateButtons]);

  /**
   * 渲染主内容
   */
  const renderContent = () => {
    const containerStyle = [
      styles.container,
      !showTopDivider && { borderTopWidth: 0 },
      style,
    ];

    return (
      <View style={containerStyle}>
        {renderLeftIcons()}
        {renderRightButtons()}
      </View>
    );
  };

  // 如果需要安全区域适配且固定在底部，使用绝对定位的 wrapper
  if (safeArea && fixed) {
    return (
      <View style={styles.fixedContainer}>
        {renderContent()}
      </View>
    );
  }

  // 如果只需要安全区域适配（不固定），使用 SafeAreaView
  if (safeArea) {
    return (
      <SafeAreaView>
        {renderContent()}
      </SafeAreaView>
    );
  }

  // 如果只需要固定（不安全区域适配）
  if (fixed) {
    return (
      <View style={styles.fixedContainer}>
        {renderContent()}
      </View>
    );
  }

  return renderContent();
};

// 默认导出
export default DbProductActionBar;

// 导出类型
export type {
  DbProductActionBarProps,
  ActionBarIconItem,
  ActionBarButton,
} from './DbProductActionBar.types';
